import React, { useState } from 'react';
import { CodeMirrorCell } from '../components/notebook/codemirror';
import type { NotebookCell } from '../components/notebook/NotebookEditor';

const TestCodeMirror: React.FC = () => {
  const [cells, setCells] = useState<NotebookCell[]>([
    {
      id: '1',
      type: 'code',
      source: '# Test GPU detection\nimport torch\nprint(f"CUDA available: {torch.cuda.is_available()}")\nprint(f"GPU count: {torch.cuda.device_count()}")',
      outputs: [],
      execution_count: null,
    },
    {
      id: '2',
      type: 'code',
      source: '# Test data science imports\nimport numpy as np\nimport pandas as pd\nimport matplotlib.pyplot as plt\n\n# Create sample data\ndata = pd.DataFrame({\n    "x": np.arange(10),\n    "y": np.random.randn(10)\n})',
      outputs: [],
      execution_count: null,
    },
  ]);
  const [activeCell, setActiveCell] = useState<string>('1');

  const handleExecute = async (cellId: string, code: string) => {
    console.log(`Executing cell ${cellId}:`, code);
    // Simulate execution
    setCells(prev => prev.map(cell => 
      cell.id === cellId 
        ? { 
            ...cell, 
            execution_count: (cell.execution_count || 0) + 1,
            outputs: [{
              type: 'stream',
              text: `Executed: ${code.substring(0, 50)}...`
            }]
          }
        : cell
    ));
  };

  const handleCellChange = (cellId: string, newSource: string) => {
    setCells(prev => prev.map(cell => 
      cell.id === cellId ? { ...cell, source: newSource } : cell
    ));
  };

  const handleAddCell = (position: 'above' | 'below', relativeTo: string) => {
    const index = cells.findIndex(c => c.id === relativeTo);
    const newCell: NotebookCell = {
      id: Date.now().toString(),
      type: 'code',
      source: '',
      outputs: [],
      execution_count: null,
    };
    
    const newCells = [...cells];
    if (position === 'above') {
      newCells.splice(index, 0, newCell);
    } else {
      newCells.splice(index + 1, 0, newCell);
    }
    setCells(newCells);
    setActiveCell(newCell.id);
  };

  const handleDeleteCell = (cellId: string) => {
    if (cells.length > 1) {
      setCells(prev => prev.filter(c => c.id !== cellId));
    }
  };

  const handleMoveCell = (cellId: string, direction: 'up' | 'down') => {
    const index = cells.findIndex(c => c.id === cellId);
    if ((direction === 'up' && index > 0) || (direction === 'down' && index < cells.length - 1)) {
      const newCells = [...cells];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newCells[index], newCells[targetIndex]] = [newCells[targetIndex], newCells[index]];
      setCells(newCells);
    }
  };

  const handleNavigate = (fromCellId: string, direction: 'up' | 'down') => {
    const index = cells.findIndex(c => c.id === fromCellId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < cells.length) {
      setActiveCell(cells[targetIndex].id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-orange-500">CodeMirror 6 Test Page</h1>
        <p className="text-gray-400 mb-8">
          Testing the new CodeMirror 6 implementation with PropAlgos theme and Python enhancements.
        </p>
        
        <div className="space-y-4">
          {cells.map((cell) => (
            <CodeMirrorCell
              key={cell.id}
              cell={cell}
              isActive={activeCell === cell.id}
              isExecuting={false}
              onExecute={(code) => handleExecute(cell.id, code)}
              onChange={(code) => handleCellChange(cell.id, code)}
              onFocus={() => setActiveCell(cell.id)}
              onDelete={() => handleDeleteCell(cell.id)}
              onAddCellAbove={() => handleAddCell('above', cell.id)}
              onAddCellBelow={() => handleAddCell('below', cell.id)}
              onMoveUp={() => handleMoveCell(cell.id, 'up')}
              onMoveDown={() => handleMoveCell(cell.id, 'down')}
              onChangeCellType={(type) => {
                setCells(prev => prev.map(c => 
                  c.id === cell.id ? { ...c, type } : c
                ));
              }}
              onNavigateUp={() => handleNavigate(cell.id, 'up')}
              onNavigateDown={() => handleNavigate(cell.id, 'down')}
            />
          ))}
        </div>
        
        <div className="mt-8 p-4 bg-gray-900 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Keyboard Shortcuts</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-orange-400">Shift+Enter:</span> Run cell and move to next
            </div>
            <div>
              <span className="text-orange-400">Ctrl/Cmd+Enter:</span> Run cell in place
            </div>
            <div>
              <span className="text-orange-400">Alt+Enter:</span> Run cell and insert below
            </div>
            <div>
              <span className="text-orange-400">Escape:</span> Exit edit mode
            </div>
            <div>
              <span className="text-orange-400">a:</span> Insert cell above
            </div>
            <div>
              <span className="text-orange-400">b:</span> Insert cell below
            </div>
            <div>
              <span className="text-orange-400">m:</span> Change to Markdown
            </div>
            <div>
              <span className="text-orange-400">y:</span> Change to Code
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCodeMirror;